<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div class="site-grid-container" v-if="favoriteSites.length > 0">
    <div v-for="site in favoriteSites" :key="site.id" class="site-card">
      <div class="site-card-row">
        <span
          class="visibility-dot"
          :class="'visibility-dot--' + getVisibilitySeverity(site.visibility)"
          :aria-label="getVisibilityLabel(site.visibility)"
          v-tooltip.bottom="getVisibilityLabel(site.visibility)"
        ></span>
        <div>
          <div
            class="site-title"
            @click="goToSite(site)"
            v-tooltip.bottom="site.title"
          >
            {{ site.title }}
          </div>
          <div class="site-description">
            {{ site.description || "Aucune description" }}
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else>{{ $t("emptyFavoriteSite") }}</div>
</template>

<script>
import { useConfigStore } from "@/stores/config";
import { useFavoritesStore } from "@/stores/favorites";
import { mapState } from "pinia";
import Tooltip from "primevue/tooltip";

export default {
  name: "FavoriteSiteList",
  directives: {
    tooltip: Tooltip,
  },
  props: {
    urlResolver: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      favorites: [],
    };
  },
  methods: {
    getSite(fav) {
      return fav.target.site;
    },
    goToSite(site) {
      if (this.urlResolver) {
        this.urlResolver(site);
      } else {
        window.open(
          `${this.ESPACES_HOST}/espaces/mes-espaces/${site.id}`,
          "_blank",
        );
      }
    },
    getVisibilityLabel(visibility) {
      switch (visibility) {
        case "PUBLIC":
          return "Public";
        case "PRIVATE":
          return "Privé";
        case "MODERATED":
          return "Modéré";
        default:
          return "Inconnu";
      }
    },
    getVisibilitySeverity(visibility) {
      switch (visibility) {
        case "PUBLIC":
          return "success"; // vert
        case "PRIVATE":
          return "danger"; // rouge
        case "MODERATED":
          return "warning"; // orange
        default:
          return "info";
      }
    },
  },
  mounted() {
    const favoritesStore = useFavoritesStore();
    favoritesStore
      .loadFavorites()
      .then(() => {
        this.favorites = favoritesStore.siteFavorites;
      })
      .catch((err) => {
        console.error("Erreur lors du chargement des favoris:", err);
      });
  },
  computed: {
    ...mapState(useConfigStore, ["ESPACES_HOST"]),
    favoriteSites() {
      // Parcours chaque favorite, récupère target.site si existant
      return this.favorites
        .map((fav) => this.getSite(fav))
        .filter((site) => site !== undefined && site !== null)
        .sort((a, b) => a.title.localeCompare(b.title));
    },
  },
};
</script>
<style scoped>
.site-grid-container {
  display: grid;
  gap: 0.5rem;
  grid-template-columns: 1fr;
}

.site-card {
  background-color: var(--p-content-background);
  padding: 0.5rem 0.75rem;
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.site-card-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.site-title {
  display: flex;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
}

.site-description {
  display: flex;
  font-size: 0.75rem;
  color: var(--p-text-muted-color);
  margin-top: 0.25rem;
}

.visibility-dot {
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 50%;
  flex-shrink: 0;
}

.visibility-dot--success {
  background-color: var(--p-green-500);
}

.visibility-dot--danger {
  background-color: var(--p-red-500);
}

.visibility-dot--warning {
  background-color: var(--p-orange-500);
}

.visibility-dot--info {
  background-color: var(--p-blue-500);
}
</style>
